import React, { useState } from "react";
import { createRequest, REQUEST_SERVICES } from "../lib/requests";
import { Modal, toast } from "./UI";

export default function RequestModal({ user, packages, onClose, onSaved }) {
  const [packageId,  setPackageId]  = useState(packages[0]?.id || "");
  const [selected,   setSelected]   = useState([]);
  const [note,       setNote]       = useState("");
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");

  const toggleService = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const totalCost = REQUEST_SERVICES
    .filter(s => selected.includes(s.id))
    .reduce((sum, s) => sum + s.price, 0);

  const needsNote = selected.includes("other") || selected.includes("report_mistake");

  const save = async () => {
    if (selected.length === 0) { setError("Please select at least one service."); return; }
    if (needsNote && !note.trim()) { setError("Please describe your request in the note field."); return; }
    setSaving(true); setError("");
    try {
      const pkg = packages.find(p => p.id === packageId);
      await createRequest({
        customerEmail: user.email,
        customerName:  user.name,
        packageId,
        packageDesc:   pkg?.description || "",
        services:      selected,
        totalCost,
        note,
      });
      toast("Request submitted!");
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Special request" onClose={onClose} maxWidth={500}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
        Select the services you need for your package. We will process your request as soon as possible.
      </p>
      {error && <div className="error-msg">{error}</div>}

      {packages.length > 0 && (
        <div className="form-row">
          <label>Select package</label>
          <select value={packageId} onChange={e => setPackageId(e.target.value)}>
            {packages.map(p => (
              <option key={p.id} value={p.id}>
                {p.id} · {p.description || "Package"} ({p.status})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-row">
        <label>Services</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {REQUEST_SERVICES.map(svc => {
            const isSelected = selected.includes(svc.id);
            return (
              <div
                key={svc.id}
                onClick={() => toggleService(svc.id)}
                style={{
                  border: `1.5px solid ${isSelected ? "var(--green)" : "var(--border-md)"}`,
                  borderRadius: "var(--radius)", padding: "10px 12px", cursor: "pointer",
                  background: isSelected ? "var(--green-light)" : "var(--bg)",
                  transition: "all .15s", display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <span style={{ fontSize: 18 }}>{svc.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isSelected ? "var(--green-dark)" : "var(--text)" }}>
                    {svc.label}
                  </div>
                  <div style={{ fontSize: 12, color: isSelected ? "var(--green-dark)" : "var(--text-muted)" }}>
                    {svc.price > 0 ? `$${svc.price}` : "Free"}
                  </div>
                </div>
                {isSelected && <span style={{ color: "var(--green)", fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="form-row">
        <label>{needsNote ? "Note (required)" : "Additional note (optional)"}</label>
        <textarea
          rows={3}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={needsNote ? "Please describe your request..." : "Any additional details..."}
        />
      </div>

      {/* Cost summary */}
      {selected.length > 0 && (
        <div style={{ background: totalCost > 0 ? "var(--green-light)" : "var(--bg-soft)", borderRadius: "var(--radius)", padding: "12px 14px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: totalCost > 0 ? "var(--green-dark)" : "var(--text-muted)" }}>
            {selected.length} service{selected.length !== 1 ? "s" : ""} selected
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: totalCost > 0 ? "var(--green-dark)" : "var(--text-muted)" }}>
            {totalCost > 0 ? `$${totalCost} total` : "Free"}
          </span>
        </div>
      )}

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Submitting..." : "Submit request"}
        </button>
      </div>
    </Modal>
  );
}
